import { applyCleValidationErrorsToFormik } from "@/api/cle-api/errors";
import Button from "@/components/bricks/button/Button";
import { CleApiErrorAlert } from "@/components/cle-api/CleApiErrorAlert";
import ValidationErrorMessage from "@/components/formcontrols/ValidationErrorMessage";
import EditIcon from "@/components/icons/EditIcon";
import Modal, {
  ModalContext,
  ModalProvider,
  ModalTrigger,
} from "@/components/Modal/Modal";
import Spinner from "@/components/spinner/Spinner";
import { StickyFooter } from "@/components/stickyFooter/modalStickyFooter";
import type {
  AuthorshipEvidenceModel,
  UpdateAuthorshipRequest,
} from "@/api/cle-api";
import { useUpdateAuthorship } from "@/hooks/cle-hooks";
import { Form, FormikProvider, useFormik } from "formik";
import { useContext, useMemo } from "react";
import * as Yup from "yup";
import { YupNumberOfLessonsNullable } from "../../../util/yup";

export interface AuthorshipValidationFormValues {
  bookTitle: string;
  publisher: string;
  publicationYear: string;
  isbn: string;
  numberOfLessons: string;
}

const validationSchema = Yup.object({
  bookTitle: Yup.string().trim(),
  publisher: Yup.string().trim(),
  publicationYear: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .integer("Udgivelsesår skal være et helt tal")
    .typeError("Udgivelsesår skal være et tal"),
  isbn: Yup.string().trim(),
  numberOfLessons: YupNumberOfLessonsNullable(),
});

export const authorshipToValidationFormValues = (
  authorship: AuthorshipEvidenceModel,
): AuthorshipValidationFormValues => ({
  bookTitle: authorship.bookTitle ?? "",
  publisher: authorship.publisher ?? "",
  publicationYear:
    authorship.publicationYear != null
      ? String(authorship.publicationYear)
      : "",
  isbn: authorship.isbn ?? "",
  numberOfLessons:
    authorship.numberOfLessons != null
      ? String(authorship.numberOfLessons)
      : "",
});

export const validationFormValuesToUpdateRequest = (
  values: AuthorshipValidationFormValues,
  initialValues: AuthorshipValidationFormValues,
): UpdateAuthorshipRequest => {
  const body: UpdateAuthorshipRequest = {};

  const bookTitle = values.bookTitle.trim();
  if (bookTitle !== initialValues.bookTitle.trim()) {
    body.book_title = bookTitle;
  }

  const publisher = values.publisher.trim();
  if (publisher !== initialValues.publisher.trim()) {
    body.publisher = publisher;
  }

  const publicationYear =
    values.publicationYear === "" ? undefined : Number(values.publicationYear);
  const initialPublicationYear =
    initialValues.publicationYear === ""
      ? undefined
      : Number(initialValues.publicationYear);
  if (publicationYear !== initialPublicationYear) {
    body.publication_year = publicationYear;
  }

  const isbn = values.isbn.trim() || null;
  const initialIsbn = initialValues.isbn.trim() || null;
  if (isbn !== initialIsbn) {
    body.isbn = isbn;
  }

  const numberOfLessons =
    values.numberOfLessons === "" ? undefined : Number(values.numberOfLessons);
  const initialNumberOfLessons =
    initialValues.numberOfLessons === ""
      ? undefined
      : Number(initialValues.numberOfLessons);
  if (numberOfLessons !== initialNumberOfLessons) {
    body.number_of_lessons = numberOfLessons;
  }

  return body;
};

interface AuthorshipValidationEditFormProps {
  authorship: AuthorshipEvidenceModel;
  onSave: (updated: AuthorshipEvidenceModel) => void;
}

const AuthorshipValidationEditForm = ({
  authorship,
  onSave,
}: AuthorshipValidationEditFormProps) => {
  const { setModalOpen } = useContext(ModalContext);
  const {
    mutate: updateAuthorship,
    isPending,
    isError,
    error,
    reset: resetMutation,
  } = useUpdateAuthorship();

  const initialFormValues = useMemo(
    () => authorshipToValidationFormValues(authorship),
    [authorship],
  );

  const formik = useFormik<AuthorshipValidationFormValues>({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const body = validationFormValuesToUpdateRequest(
        values,
        initialFormValues,
      );
      if (Object.keys(body).length === 0) return;

      updateAuthorship(
        {
          authorshipId: authorship.id,
          userId: authorship.ownerUserId,
          body,
        },
        {
          onSuccess: (updated) => {
            onSave(updated);
            resetMutation();
            setModalOpen(false);
          },
          onError: (err) => {
            applyCleValidationErrorsToFormik(err, formik.setFieldError);
          },
        },
      );
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    resetMutation();
    setModalOpen(false);
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <div className="c-modal-container">
          <div className="c-item-list c-modal-container-inner">
            <div className="flex-100">
              <label className="form-label" htmlFor="bookTitle">
                Titel
              </label>
              <input
                autoComplete="off"
                className="form-control"
                id="bookTitle"
                name="bookTitle"
                type="text"
                value={formik.values.bookTitle}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="publisher">
                Forlag
              </label>
              <input
                autoComplete="off"
                className="form-control"
                id="publisher"
                name="publisher"
                type="text"
                value={formik.values.publisher}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="publicationYear">
                Udgivelsesår
              </label>
              <input
                autoComplete="off"
                className={`form-control${
                  formik.touched.publicationYear &&
                  formik.errors.publicationYear
                    ? " is-invalid"
                    : ""
                }`}
                id="publicationYear"
                name="publicationYear"
                step="1"
                type="number"
                value={formik.values.publicationYear}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
              <ValidationErrorMessage
                touched={formik.touched.publicationYear}
                errorMessage={formik.errors.publicationYear}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="isbn">
                ISBN
              </label>
              <input
                autoComplete="off"
                className="form-control"
                id="isbn"
                name="isbn"
                type="text"
                value={formik.values.isbn}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="numberOfLessons">
                Antal lektioner
              </label>
              <input
                autoComplete="off"
                className={`form-control${
                  formik.touched.numberOfLessons &&
                  formik.errors.numberOfLessons
                    ? " is-invalid"
                    : ""
                }`}
                id="numberOfLessons"
                min="0"
                name="numberOfLessons"
                step="any"
                type="number"
                value={formik.values.numberOfLessons}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
              <ValidationErrorMessage
                touched={formik.touched.numberOfLessons}
                errorMessage={formik.errors.numberOfLessons}
              />
            </div>
          </div>
          {isError && (
            <div className="c-modal-container-inner">
              <CleApiErrorAlert
                error={error}
                fallback="Kunne ikke gemme opdatering."
              />
            </div>
          )}
          <StickyFooter className="border-top">
            <Button color="secondary" type="button" onClick={handleCancel}>
              Annuller
            </Button>
            <div className="d-flex gap-2 align-items-center">
              {isPending && <Spinner size={24} />}
              <Button
                color="primary"
                disabled={isPending || !formik.dirty}
                type="submit"
              >
                Gem
              </Button>
            </div>
          </StickyFooter>
        </div>
      </Form>
    </FormikProvider>
  );
};

interface AuthorshipValidationEditModalProps {
  authorship: AuthorshipEvidenceModel;
  onSave: (updated: AuthorshipEvidenceModel) => void;
}

export const AuthorshipValidationEditModal = ({
  authorship,
  onSave,
}: AuthorshipValidationEditModalProps) => (
  <ModalProvider>
    <ModalTrigger className="btn btn-primary btn--transparent has-icon px-2 edit-trigger">
      Redigér
      <EditIcon height={16} />
    </ModalTrigger>
    <Modal title="Redigér forfatterskab">
      <AuthorshipValidationEditForm authorship={authorship} onSave={onSave} />
    </Modal>
  </ModalProvider>
);
